import React from 'react';
import { Empty } from 'antd';
import { useCart } from 'stores/cartPOSContext';
import { formatMoney } from 'utils';
import { CartItemCard } from '../Card/CartItemCard';
import { ButtonCommon, showAlertNotification } from '..';
import SaleOrderPaymentInfo from './SaleOrderPaymentInfo';
import { CartStatus } from 'types/common';

export const CartPanel: React.FC = () => {
  const { state, updateCartStatus, removeCart, getActiveCart } = useCart();
  const { carts } = state;
  const activeCart = getActiveCart();
  const [lastAddedItemId, setLastAddedItemId] = React.useState<string | null>(null);
  const prevItemIdsRef = React.useRef<string[]>([]);
  const isInitializedRef = React.useRef<boolean>(false);
  const prevCartIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!activeCart) return;

    const currentIds = activeCart.items.map((item) => item.id);

    if (prevCartIdRef.current !== activeCart.id) {
      prevCartIdRef.current = activeCart.id;
      prevItemIdsRef.current = currentIds;
      setLastAddedItemId(null);
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }
      return;
    }

    if (!isInitializedRef.current) {
      prevItemIdsRef.current = currentIds;
      isInitializedRef.current = true;
      return;
    }

    const addedItemId = currentIds.find((id) => !prevItemIdsRef.current.includes(id));
    if (addedItemId) {
      setLastAddedItemId(addedItemId);
    }
    prevItemIdsRef.current = currentIds;
  }, [activeCart?.id, activeCart?.items]);

  React.useEffect(() => {
    if (activeCart?.items.length === 0) {
      updateCartStatus(activeCart.id, CartStatus.PRODUCT_SELECTION);
    }
  }, [activeCart?.items.length]);
  if (!activeCart) {
    return <Empty description='Không có giỏ hàng nào được chọn' image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const handleRemoveCart = () => {
    if (carts.length === 0) {
      showAlertNotification({
        type: 'warning',
        title: 'Thông báo',
        message: 'Không có giỏ hàng nào để xóa.',
      });
      return;
    }
    if (carts.length === 1) {
      showAlertNotification({
        type: 'warning',
        title: 'Thông báo',
        message: 'Không thể xóa giỏ hàng, bạn cần ít nhất 1 giỏ hàng.',
      });
      return;
    }
    removeCart(activeCart.id);
  };

  const handleProceedToPayment = () => {
    if (activeCart.items.length === 0) {
      showAlertNotification({
        type: 'warning',
        title: 'Thông báo',
        message: 'Vui lòng chọn ít nhất một sản phẩm.',
      });
      return;
    }
    const invalidItems = activeCart.items.filter((item) => item.quantity <= 0 || !item.unitPrice);
    if (invalidItems.length > 0) {
      showAlertNotification({
        type: 'warning',
        title: 'Thông báo',
        message: `Vui lòng kiểm tra lại số lượng của các sản phẩm ${invalidItems.map((item) => `\n- ${item.productVersion.name} ${item.productVersion.color} ${item.productVersion.dimension}`).join('')}`,
      });
      return;
    }
    updateCartStatus(activeCart.id, CartStatus.PAYMENT);
  };
  return (
    <div className='flex flex-col h-full'>
      {activeCart.status !== CartStatus.COMPLETED ? (
        <>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-[20px] leading-[24px] font-bold text-[var(--textBlack56)] '>SẢN PHẨM VỪA THÊM</h2>
            <ButtonCommon
              color='none'
              className='text-[var(--primaryColor500)]'
              size='sm'
              radius='rounded'
              onClick={handleRemoveCart}
            >
              Xóa giỏ hàng
            </ButtonCommon>
          </div>

          <div className='flex-1 overflow-auto'>
            {activeCart.items.length === 0 ? (
              <Empty description='Chưa có sản phẩm nào trong giỏ hàng' image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div className='space-y-4'>
                {activeCart.items.map((item) => (
                  <CartItemCard key={item.id} item={item} autoFocusQuantity={item.id === lastAddedItemId} />
                ))}
              </div>
            )}
          </div>

          <div className='mt-2'>
            <div
              className={
                ' px-8 bg-[var(--bgGreyColor4)] rounded-lg  ' +
                (activeCart.status === CartStatus.PRODUCT_SELECTION ? ' py-2 mb-4' : ' py-4')
              }
            >
              <div className='flex justify-center gap-[60px] text-sm text-[16px] leading-[24px] font-bold'>
                <span className='text-[var(--textBlack56)]'>Tạm tính</span>
                <span className=' text-[var(--sematicSuccessColor500)] flex gap-2'>
                  {formatMoney(activeCart.totalAmount.toString())}
                  <span className='opacity-50'> VNĐ</span>
                </span>
              </div>
            </div>

            {activeCart.status === CartStatus.PRODUCT_SELECTION && (
              <ButtonCommon
                color='primary'
                fullWidth
                radius='rounded'
                onClick={handleProceedToPayment}
                isDisabled={activeCart.items.length === 0}
              >
                Nhập thông tin đơn hàng
              </ButtonCommon>
            )}
          </div>
        </>
      ) : (
        <SaleOrderPaymentInfo saleOrderId={activeCart.saleOrderId || ''} />
      )}
    </div>
  );
};
